'use client'

import { memo, useState } from 'react'

import { isEqual } from 'lodash'
import {
  Download,
  ExternalLink,
  Info,
  MinusCircle,
  PlusCircle,
  Send,
  Upload,
  Zap,
} from 'lucide-react'
import { useInterval } from 'react-use'

import { ShowMoreToggle } from '~/components/ShowMoreToggle'
import { NetworkDisplay, Pill, Skeleton } from '~/components/component-library'
import { Dropdown } from '~/components/component-library/Dropdown'
import { EarnFormDialogContent } from '~/components/forms/earn/EarnDialog'
import { buildSwapQueryParams } from '~/components/forms/swap/SwapButton'
import { buildSendQueryParams } from '~/components/forms/transfer/SendButton'
import { TokenSelectorOption } from '~/components/smart-accounts/TokenSelectorOption'
import { buildTokenInfoDropdownItems } from '~/components/smart-accounts/tokenDropdownItems'
import { USDC_TOKENS } from '~/constants/addresses'
import { SupportedChainId } from '~/constants/chains'
import { EARN_GUIDE_URL } from '~/constants/externalUrls'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrg } from '~/hooks/org'
import { useNavigation } from '~/hooks/useNavigation'
import { isAutomationAccount } from '~/hooks/useSmartAccountPermissions'
import {
  SmartAccountBalancesGroupBy,
  useOrgSmartAccount,
  useSmartAccountDisplayedBalances,
} from '~/hooks/useSmartAccounts'
import { useVaultApy } from '~/hooks/useTokens'
import { useCanCreateTransaction } from '~/hooks/useTransactions'
import { EarnAction, IAddress } from '~/types'
import { isSwapChainId } from '~/utils'
import { Address } from '~/utils/address'
import { getExplorerAddressLinkToken } from '~/utils/addresses'
import {
  AAVE_BASE_USDC,
  getAutoEarnModule,
  getEarnLabel,
  getVaultAsset,
  getEarnDepositVaults,
  isLegacyVault,
  isVaultToken,
} from '~/utils/vault'

import type {
  DropdownEntry,
  DropdownItem,
} from '~/components/component-library/Dropdown'

const MAX_DISPLAYED_BALANCES = 3

const NETWORKS_SETTINGS_PATH = '/settings/team/networks'

const swapActions = [
  { direction: 'buy', label: 'Buy', Icon: PlusCircle },
  { direction: 'sell', label: 'Sell', Icon: MinusCircle },
] as const

type SetGlobalModalState = ReturnType<typeof useAuth>['setGlobalModalState']

const buildPositionDropdownItems = ({
  isInactiveChain,
  canTransact,
  chainId,
  token,
  smartAccountAddress,
  setGlobalModalState,
  closeGlobalModal,
}: {
  isInactiveChain: boolean
  canTransact: boolean
  chainId: SupportedChainId
  token: IAddress
  smartAccountAddress: IAddress
  setGlobalModalState: SetGlobalModalState
  closeGlobalModal: () => void
}): DropdownEntry[] => {
  const openEarnModal = (action: EarnAction, vault: IAddress) =>
    setGlobalModalState({
      isOpen: true,
      content: (
        <EarnFormDialogContent
          defaultAccount={smartAccountAddress}
          vaultToken={vault}
          assetToken={getVaultAsset(chainId, vault)!}
          chainId={chainId}
          action={action}
          setIsOpen={closeGlobalModal}
        />
      ),
    })

  const explorerPath = getExplorerAddressLinkToken(
    smartAccountAddress,
    chainId,
    token,
  )

  return [
    ...(canTransact
      ? ([
          ...(!isLegacyVault(token)
            ? [
                {
                  content: 'Deposit',
                  icon: <Download />,
                  disabled: isInactiveChain,
                  tooltip: isInactiveChain ? 'Chain is inactive' : undefined,
                  onClick: () => openEarnModal(EarnAction.DEPOSIT, token),
                } as DropdownItem,
              ]
            : []),
          {
            content: 'Withdraw',
            icon: <Upload />,
            disabled: isInactiveChain,
            tooltip: isInactiveChain ? 'Chain is inactive' : undefined,
            onClick: () => openEarnModal(EarnAction.WITHDRAW, token),
          },
          'divider',
        ] as DropdownEntry[])
      : []),
    {
      content: 'Open in explorer',
      icon: <ExternalLink />,
      href: explorerPath ? { path: explorerPath, newTab: true } : undefined,
    },
    {
      content: 'More info',
      icon: <Info />,
      href: { path: EARN_GUIDE_URL, newTab: true },
    },
  ]
}

const buildBalanceDropdownItems = ({
  isInactiveChain,
  canTransact,
  chainId,
  token,
  smartAccountAddress,
  availableVaults,
  apy,
  setGlobalModalState,
  closeGlobalModal,
  navigate,
}: {
  isInactiveChain: boolean
  canTransact: boolean
  chainId: SupportedChainId
  token: IAddress
  smartAccountAddress: IAddress
  availableVaults: IAddress[]
  apy: number | undefined
  setGlobalModalState: SetGlobalModalState
  closeGlobalModal: () => void
  navigate: ReturnType<typeof useNavigation>
}): DropdownEntry[] => {
  const openEarnModal = (action: EarnAction, vault: IAddress) =>
    setGlobalModalState({
      isOpen: true,
      content: (
        <EarnFormDialogContent
          defaultAccount={smartAccountAddress}
          vaultToken={vault}
          assetToken={getVaultAsset(chainId, vault)!}
          chainId={chainId}
          action={action}
          setIsOpen={closeGlobalModal}
        />
      ),
    })

  const isSwapSupported = isSwapChainId(chainId)

  const actionItems: DropdownEntry[] = isInactiveChain
    ? [
        {
          // Whole row navigates to settings; the button is a visual
          // affordance only, so clicking anywhere in the row works.
          onClick: () => navigate(NETWORKS_SETTINGS_PATH),
          content: (
            <div className="flex max-w-48 flex-col gap-1.5">
              <span className="text-sm">
                Enable <NetworkDisplay layout="inline-text" chainId={chainId} />{' '}
                to interact with this token.
              </span>
              <span className="text-sm font-medium text-blue-500">
                Go to settings →
              </span>
            </div>
          ),
        },
        'divider',
      ]
    : canTransact
      ? [
          {
            content: 'Send',
            icon: <Send />,
            href: {
              path: `/send${buildSendQueryParams({
                defaultSender: smartAccountAddress,
                defaultToken: token,
                defaultChainId: chainId,
              })}`,
            },
          },
          ...swapActions.map(({ direction, label, Icon }): DropdownItem => ({
            content: label,
            icon: <Icon />,
            disabled: !isSwapSupported,
            tooltip: isSwapSupported
              ? undefined
              : 'Chain not supported for swapping',
            href: {
              path: `/swap${buildSwapQueryParams({
                defaultSender: smartAccountAddress,
                defaultToken: token,
                defaultChainId: chainId,
                direction,
              })}`,
            },
          })),
          ...(availableVaults.length > 0
            ? ([
                {
                  content: getEarnLabel(apy),
                  icon: <Zap />,
                  onClick: () =>
                    openEarnModal(EarnAction.DEPOSIT, availableVaults[0]),
                },
              ] as DropdownEntry[])
            : []),
          'divider',
        ]
      : []

  return [
    ...actionItems,
    ...buildTokenInfoDropdownItems({
      token,
      chainId,
      holderAddress: smartAccountAddress,
      smartAccountAddress,
      setGlobalModalState,
      closeGlobalModal,
    }),
  ]
}

// Render with `key={pausedUntil}` so `now` starts fresh for each new pause —
// the interval is off while unpaused, so a mounted clock would be stale.
const AutoEarnPausedPill = ({ pausedUntil }: { pausedUntil: string }) => {
  const [now, setNow] = useState(Date.now)
  const minutes = Math.ceil((new Date(pausedUntil).getTime() - now) / 60_000)
  useInterval(() => setNow(Date.now()), minutes > 0 ? 30_000 : null)

  if (minutes <= 0) return null
  const [count, unit] =
    minutes >= 1440
      ? [Math.ceil(minutes / 1440), 'day']
      : minutes >= 60
        ? [Math.ceil(minutes / 60), 'hour']
        : [minutes, 'minute']
  return (
    <Pill
      label="Auto-earn paused"
      size="small"
      tooltipMessage={`Auto-earn resumes in ${count} ${unit}${count === 1 ? '' : 's'}`}
    />
  )
}

function SmartAccountBalancesToken({
  smartAccountAddress,
  token,
  chainId,
  amount,
  isPosition = false,
}: {
  smartAccountAddress: IAddress
  token: IAddress
  chainId: SupportedChainId
  amount: bigint
  isPosition?: boolean
}) {
  const { org, featureFlags } = useActiveOrg()
  const { smartAccount } = useOrgSmartAccount(smartAccountAddress)
  const canCreateTransaction = useCanCreateTransaction(smartAccount)
  const isViewOnly = isAutomationAccount(smartAccount)
  const isInactiveChain = !(
    org?.smartAccountsChainIds.includes(chainId) ?? false
  )
  const availableVaults = getEarnDepositVaults(
    chainId,
    token,
    !!featureFlags?.['workspace-auto-earn'],
  )
  const { setGlobalModalState, closeGlobalModal } = useAuth()
  const navigate = useNavigation()
  const { data: apy } = useVaultApy(chainId, availableVaults[0], {
    enabled: availableVaults.length > 0,
  })
  // Auto-earn only sweeps USDC.
  const pausedUntil =
    smartAccount && Address.equal(token, USDC_TOKENS[chainId])
      ? getAutoEarnModule(smartAccount, chainId)?.pausedUntil
      : undefined

  if (!smartAccount) {
    return null
  }

  return (
    <Dropdown
      align="start"
      side="left"
      items={
        isPosition
          ? buildPositionDropdownItems({
              isInactiveChain,
              canTransact: canCreateTransaction && !isViewOnly,
              chainId,
              token,
              smartAccountAddress: smartAccount.address,
              setGlobalModalState,
              closeGlobalModal,
            })
          : buildBalanceDropdownItems({
              isInactiveChain,
              canTransact: canCreateTransaction && !isViewOnly,
              chainId,
              token,
              smartAccountAddress: smartAccount.address,
              availableVaults,
              apy,
              setGlobalModalState,
              closeGlobalModal,
              navigate,
            })
      }
    >
      <div className="rounded-lg data-[state=open]:bg-gray-100 dark:data-[state=open]:bg-gray-800">
        <TokenSelectorOption
          token={token}
          chainId={chainId}
          balance={amount}
          suffix={
            isPosition && Address.format(token) === AAVE_BASE_USDC ? (
              <Pill label="Legacy" size="small" />
            ) : pausedUntil ? (
              <AutoEarnPausedPill key={pausedUntil} pausedUntil={pausedUntil} />
            ) : undefined
          }
          className="transition hover:cursor-pointer md:hover:bg-gray-100 md:dark:hover:bg-gray-800"
        />
      </div>
    </Dropdown>
  )
}

export const SmartAccountBalances = memo(
  function SmartAccountBalances({
    address,
    isPosition = false,
    showSmallBalances,
  }: {
    showSmallBalances: boolean
    address: IAddress
    isPosition?: boolean
  }) {
    const { smartAccount, isLoading: isLoadingSmartAccount } =
      useOrgSmartAccount(address)
    const [showAll, setShowAll] = useState(false)
    const { balances: groupedBalances, isLoading } =
      useSmartAccountDisplayedBalances(address, {
        showSmallBalances,
        balancesGroupBy: SmartAccountBalancesGroupBy.Chain,
        tokenFilter: isPosition
          ? (chainId: number, tokenAddress: IAddress) =>
              isVaultToken(chainId, tokenAddress)
          : (chainId: number, address: IAddress) =>
              !isVaultToken(chainId, address),
      })
    const balances = groupedBalances
      .map(({ groupBalances }) => groupBalances)
      .flat()
      .sort((a, b) => (b.usdValue ?? 0) - (a.usdValue ?? 0))

    if (
      !isLoading &&
      !isLoadingSmartAccount &&
      ((isPosition && balances.length === 0) || !smartAccount)
    ) {
      return null
    }

    return (
      <div className="flex flex-col space-y-2">
        <div className="-mx-3 flex flex-col">
          {isLoading ? (
            <div className="flex flex-col space-y-2 px-2">
              <Skeleton className="h-[52px]" />
            </div>
          ) : balances.length > 0 && smartAccount?.address ? (
            <>
              {balances
                .slice(0, showAll ? balances.length : MAX_DISPLAYED_BALANCES)
                .map(({ chainId, address, amount }) => (
                  <SmartAccountBalancesToken
                    key={`${chainId}-${address}`}
                    smartAccountAddress={smartAccount.address}
                    token={address}
                    chainId={chainId}
                    amount={amount}
                    isPosition={isPosition}
                  />
                ))}
              {balances.length > MAX_DISPLAYED_BALANCES && (
                <div className="flex justify-center">
                  <ShowMoreToggle
                    isExpanded={showAll}
                    onToggle={() => setShowAll(!showAll)}
                    expandedLabel="View less"
                    collapsedLabel={`View ${balances.length - MAX_DISPLAYED_BALANCES} more`}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="mx-auto py-4 text-sm text-secondary">No tokens</div>
          )}
        </div>
      </div>
    )
  },
  (prevProps, newProps) => {
    const shouldntRerender = isEqual(prevProps, newProps)
    return shouldntRerender
  },
)
